import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../interceptors";

export const fetchTemplates = createAsyncThunk(
  "questionTemplate/fetch",
  async (_arg, { rejectWithValue }) => {
    try {
      const r = await axiosInstance.get("/job-posting/question-template/list");
      if (!r.data || r.data.status === false) {
        return rejectWithValue(r.data?.message || "Failed to load templates");
      }
      return r.data.templates || [];
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to load templates");
    }
  },
);

export const createTemplate = createAsyncThunk(
  "questionTemplate/create",
  async ({ form, name }, { rejectWithValue }) => {
    try {
      const body = {
        name: name || (form.questionText || "").slice(0, 80) || "Untitled template",
        questionType: form.questionType,
        responseType: form.responseType,
        questionText: form.questionText,
        questionOptions: JSON.stringify(form.questionOptions || []),
        competancy: form.competency || "",
        defaultLanguage: form.defaultLanguage || "en",
        difficulty: form.difficulty || "MEDIUM",
        responseTimeInMinutes: Number(form.responseTimeInMinutes) || 0,
        responseTimeInSeconds: Number(form.responseTimeInSeconds) || 0,
        tags: form.tags || [],
        correctOption: form.correctOption || "",
      };
      const r = await axiosInstance.post(
        "/job-posting/question-template/save",
        body,
      );
      if (!r.data || r.data.status === false) {
        return rejectWithValue(r.data?.message || "Failed to save template");
      }
      return r.data;
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to save template");
    }
  },
);

export const cloneTemplateToJob = createAsyncThunk(
  "questionTemplate/cloneToJob",
  async ({ templateId, jobId, interviewRound }, { rejectWithValue }) => {
    try {
      const r = await axiosInstance.post(
        "/job-posting/question-template/clone-to-job",
        {
          templateId: String(templateId),
          jobId: String(jobId),
          interviewRound,
        },
      );
      if (!r.data || r.data.status === false) {
        return rejectWithValue(r.data?.message || "Failed to clone template");
      }
      return r.data;
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to clone template");
    }
  },
);

const questionTemplateSlice = createSlice({
  name: "questionTemplate",
  initialState: {
    items: [],
    status: "idle",
    error: null,
    cloning: false,
    cloneError: null,
    creating: false,
    createError: null,
    lastCreatedAt: 0,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTemplates.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchTemplates.fulfilled, (state, { payload }) => {
        state.items = payload;
        state.status = "succeeded";
      })
      .addCase(fetchTemplates.rejected, (state, { payload }) => {
        state.status = "failed";
        state.error = payload || "Failed to load templates";
      })
      .addCase(cloneTemplateToJob.pending, (state) => {
        state.cloning = true;
        state.cloneError = null;
      })
      .addCase(cloneTemplateToJob.fulfilled, (state) => {
        state.cloning = false;
      })
      .addCase(cloneTemplateToJob.rejected, (state, { payload }) => {
        state.cloning = false;
        state.cloneError = payload || "Failed to clone template";
      })
      .addCase(createTemplate.pending, (state) => {
        state.creating = true;
        state.createError = null;
      })
      .addCase(createTemplate.fulfilled, (state) => {
        state.creating = false;
        state.lastCreatedAt = Date.now();
        // Force the next templates tab visit to refetch (status flip prompts re-fetch).
        state.status = "idle";
      })
      .addCase(createTemplate.rejected, (state, { payload }) => {
        state.creating = false;
        state.createError = payload || "Failed to save template";
      });
  },
});

export const selectTemplates = (s) => s.questionTemplateSliceReducer.items;
export const selectTemplatesStatus = (s) => s.questionTemplateSliceReducer.status;
export const selectTemplatesError = (s) => s.questionTemplateSliceReducer.error;
export const selectIsCloning = (s) => s.questionTemplateSliceReducer.cloning;
export const selectCloneError = (s) => s.questionTemplateSliceReducer.cloneError;
export const selectIsCreatingTemplate = (s) => s.questionTemplateSliceReducer.creating;
export const selectCreateTemplateError = (s) => s.questionTemplateSliceReducer.createError;
export const selectLastTemplateCreatedAt = (s) => s.questionTemplateSliceReducer.lastCreatedAt;

export default questionTemplateSlice.reducer;
